import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	OverrideContainerComponent,
	Store,
} from 'react-sweet-state';
import {
	createStore,
	createHook,
	createContainer,
	createActionsHook,
	defaultRegistry,
} from 'react-sweet-state';

import { fg } from '@confluence/feature-gating';

type NetworkStatus = {
	isCollabOffline: boolean;
};

export const initialNetworkStatus: NetworkStatus = {
	isCollabOffline: false,
};

export const actions = {
	setIsCollabOffline:
		(isCollabOffline: boolean) =>
		({ setState }: { setState: (state: Partial<NetworkStatus>) => void }): void => {
			setState({
				isCollabOffline,
			});
		},
};

type NetworkStatusContainerProps = {
	initialNetworkStatus?: Partial<NetworkStatus>;
};

export const networkStatusStore: Store<
	{
		isCollabOffline: boolean;
	},
	{
		setIsCollabOffline: (
			isCollabOffline: boolean,
		) => ({ setState }: { setState: (state: Partial<NetworkStatus>) => void }) => void;
	}
> = createStore({
	initialState: {
		...initialNetworkStatus,
	},
	actions,
	name: 'networkStatus',
});

export const useCollabNetworkStatus: HookFunction<
	{
		isCollabOffline: boolean;
	},
	BoundActions<
		{
			isCollabOffline: boolean;
		},
		{
			setIsCollabOffline: (
				isCollabOffline: boolean,
			) => ({ setState }: { setState: (state: Partial<NetworkStatus>) => void }) => void;
		}
	>,
	void
> = createHook(networkStatusStore, {
	selector: (state) => ({ isCollabOffline: state.isCollabOffline }),
});

export const useCollabNetworkStatusActions: HookActionsFunction<
	BoundActions<
		{
			isCollabOffline: boolean;
		},
		{
			setIsCollabOffline: (
				isCollabOffline: boolean,
			) => ({ setState }: { setState: (state: Partial<NetworkStatus>) => void }) => void;
		}
	>
> = createActionsHook(networkStatusStore);

export const NetworkStatusContainer: OverrideContainerComponent<NetworkStatusContainerProps> =
	createContainer<NetworkStatus, typeof actions, NetworkStatusContainerProps>(networkStatusStore, {
		displayName: 'NetworkStatusContainer',
		onInit:
			() =>
			({ setState }, { initialNetworkStatus }) => {
				if (initialNetworkStatus) {
					setState({
						...initialNetworkStatus,
					});
				}
			},
	});

// Non-React utilities for accessing store outside of React components
const networkStatusStoreInstance = defaultRegistry.getStore(networkStatusStore);

/**
 * Get the current offline status outside of React components
 *
 * When `platform_editor_offline_editing_wrapper_override` is enabled, this will always return `false` (online).
 *
 * @returns true if offline, false if online
 */
export const getIsCollabOffline = (): boolean => {
	if (fg('platform_editor_offline_editing_wrapper_override')) {
		return false;
	}
	return networkStatusStoreInstance.storeState.getState().isCollabOffline;
};

/**
 * Subscribe to network status changes outside of React components
 *
 * When `platform_editor_offline_editing_wrapper_override` is enabled, the listener will never be called.
 *
 * @param listener - Callback function to be called when network status changes
 * @returns Unsubscribe function
 */
export const subscribeToCollabNetworkStatus = (listener: () => void): (() => void) => {
	if (fg('platform_editor_offline_editing_wrapper_override')) {
		return () => {};
	}
	return networkStatusStoreInstance.storeState.subscribe(listener);
};
